import { useEffect, useState, useCallback, useRef } from "react";
import { apiFetch } from "../context/AuthContext";
import { fmtDate } from "../utils/time";

const B    = "#2C1A0E";
const ROSE = "#C9847A";
const fmtR = (n) => n ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

const STATUS_CONFIG = {
  paid:      { bg: "#DCFCE7", color: "#15803D" },
  pending:   { bg: "#FEF9C3", color: "#854D0E" },
  refunded:  { bg: "#FEE2E2", color: "#DC2626" },
  cancelled: { bg: "#F3F4F6", color: "#6B7280" },
};
const FULFILL_CONFIG = {
  fulfilled:   { bg: "#DCFCE7", color: "#15803D" },
  unfulfilled: { bg: "#FEF9C3", color: "#854D0E" },
  partial:     { bg: "#DBEAFE", color: "#1D4ED8" },
};

export default function OrdersPage() {
  const [orders,  setOrders]  = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [status,  setStatus]  = useState("");
  const [source,  setSource]  = useState("");
  const [dateFrom,setDateFrom]= useState("");
  const [dateTo,  setDateTo]  = useState("");
  const [page,    setPage]    = useState(1);
  const [expanded,setExpanded]= useState(null);
  const searchTimer = useRef(null);
  const LIMIT = 50;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: LIMIT });
    if (search)   params.set("search",    search);
    if (status)   params.set("status",    status);
    if (source)   params.set("source",    source);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo)   params.set("date_to",   dateTo);
    const r = await apiFetch(`/api/orders?${params}`);
    if (r.ok) {
      const d = await r.json();
      setOrders(d.orders || []);
      setTotal(d.total || 0);
    }
    setLoading(false);
  }, [search, status, source, dateFrom, dateTo]);

  useEffect(() => { setPage(1); load(1); }, [status, source, dateFrom, dateTo]);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(1); }, 350);
  }, [search]);
  useEffect(() => { load(page); }, [page]);

  const totalPages = Math.ceil(total / LIMIT);

  const totalRevenue = orders.reduce((s, o) => s + (o.status === "paid" ? (o.amount || 0) : 0), 0);

  return (
    <div style={{ padding: "28px 28px 80px", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                     color: B, fontSize: 26, fontWeight: 700, margin: 0 }}>
          🛒 Orders
        </h1>
        <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
          Online (Shopify) + Offline (Zoho) · {total.toLocaleString()} total
        </p>
      </div>

      {/* Filters row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search customer, order #, email…"
          style={{ ...inp, flex: 1, minWidth: 200 }}
        />
        <select value={status} onChange={e => setStatus(e.target.value)} style={sel}>
          <option value="">All Statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="refunded">Refunded</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={source} onChange={e => setSource(e.target.value)} style={sel}>
          <option value="">All Sources</option>
          <option value="Shopify">Shopify</option>
          <option value="Zoho">Zoho</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={sel} />
        <span style={{ color: "#aaa", alignSelf: "center" }}>→</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={sel} />
        {(dateFrom || dateTo || status || source || search) && (
          <button onClick={() => { setSearch(""); setStatus(""); setSource(""); setDateFrom(""); setDateTo(""); }}
            style={{ background: "none", border: "1px solid #DDD4C8", borderRadius: 7,
                     padding: "6px 12px", fontSize: 12, color: "#888", cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      {/* Summary strip for current filtered view */}
      {orders.length > 0 && (
        <div style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
          Showing {orders.length} of {total} orders
          {totalRevenue > 0 && <> · Paid revenue in view: <strong style={{ color: B }}>{fmtR(totalRevenue)}</strong></>}
        </div>
      )}

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #EDE0D4", borderRadius: 12, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#F7F5F2" }}>
              <th style={th}>Order</th>
              <th style={th}>Customer</th>
              <th style={th}>Date</th>
              <th style={th}>Source</th>
              <th style={th}>Status</th>
              <th style={th}>Fulfillment</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding: 24, color: "#aaa", textAlign: "center" }}>Loading…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, color: "#aaa", textAlign: "center" }}>
                No orders found
              </td></tr>
            )}
            {!loading && orders.map(o => {
              const sc = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending;
              const fc = FULFILL_CONFIG[o.fulfillment_status] || FULFILL_CONFIG.unfulfilled;
              return (
                <>
                  <tr key={o._id}
                    onClick={() => setExpanded(expanded === o._id ? null : o._id)}
                    style={{ borderBottom: "1px solid #F0E8E0", cursor: "pointer",
                             background: expanded === o._id ? "#FDF8F5" : "#fff" }}>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 12, color: "#555" }}>
                      <span style={{ marginRight: 6, fontSize: 10, color: "#aaa" }}>
                        {expanded === o._id ? "▼" : "▶"}
                      </span>
                      {o.shopify_order_no || o._id?.slice(-6) || "—"}
                    </td>
                    <td style={{ ...td, fontWeight: 600, color: B }}>
                      {o.customer_name || "—"}
                      {o.customer_email && (
                        <div style={{ fontSize: 11, color: "#888", fontWeight: 400 }}>
                          {o.customer_email}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: "#555" }}>
                      {fmtDate(o.order_date)}
                    </td>
                    <td style={td}>
                      <span style={{
                        background: o.source === "Shopify" ? "#DBEAFE" : "#F0FDF4",
                        color: o.source === "Shopify" ? "#1D4ED8" : "#15803D",
                        borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                      }}>{o.source || o.channel || "Manual"}</span>
                    </td>
                    <td style={td}>
                      <span style={{ ...sc, borderRadius: 4, padding: "2px 8px",
                                     fontSize: 11, fontWeight: 600 }}>
                        {o.status}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ ...fc, borderRadius: 4, padding: "2px 8px",
                                     fontSize: 11, fontWeight: 600 }}>
                        {o.fulfillment_status || "unfulfilled"}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700, color: B }}>
                      {fmtR(o.amount)}
                    </td>
                  </tr>
                  {expanded === o._id && (
                    <tr key={`${o._id}-detail`}>
                      <td colSpan={7} style={{ padding: "12px 24px 16px", background: "#FDF8F5",
                                               borderBottom: "1px solid #F0E8E0" }}>
                        {/* Line items */}
                        {(o.line_items || []).length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#888",
                                          textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                              Items
                            </div>
                            {o.line_items.map((li, i) => (
                              <div key={i} style={{ display: "flex", gap: 12, fontSize: 12,
                                                    color: "#555", marginBottom: 4 }}>
                                <span style={{ fontWeight: 600 }}>{li.title}</span>
                                {li.sku && <span style={{ color: "#aaa" }}>{li.sku}</span>}
                                <span>×{li.quantity}</span>
                                <span>{fmtR(li.price)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 24, fontSize: 12, color: "#888", flexWrap: "wrap" }}>
                          {o.shipping_city && <span>📍 {o.shipping_city}</span>}
                          {o.note && <span>📝 {o.note}</span>}
                          {o.dispatch_date && <span>🚚 Dispatched: {fmtDate(o.dispatch_date)}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={pgBtn}>← Prev</button>
          <span style={{ padding: "6px 12px", fontSize: 13, color: "#888" }}>
            {page} / {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} style={pgBtn}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

const inp = { border:"1px solid #DDD4C8", borderRadius:7, padding:"7px 12px",
              fontSize:13, background:"#FAF7F2", color:"#2C1A0E" };
const sel = { ...inp, minWidth: 130 };
const th  = { padding:"9px 14px", textAlign:"left", fontWeight:600, color:"#666",
              fontSize:11, textTransform:"uppercase", letterSpacing:0.5 };
const td  = { padding:"10px 14px", verticalAlign:"middle" };
const pgBtn = { background:"#F0EBE3", color:"#2C1A0E", border:"1px solid #DDD4C8",
                borderRadius:7, padding:"6px 14px", fontSize:12, cursor:"pointer" };
